import { Box, Link } from "@mui/material";
import { useEffect, type JSX, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { Backdrop } from "../login/Backdrop.js";
import { SiteFooter } from "../login/SiteFooter.js";
import { TopBar } from "../login/TopBar.js";
import { SITE_MAP } from "./content.js";
import { SiteNav } from "./SiteNav.js";

/**
 * The public site's frame: the sign-in screen's chrome (backdrop, white
 * top bar, contact footer) plus the site navigation in the bar and the
 * link columns in the footer.
 *
 * `/`, `/reseller-program`, `/login` and `/register` all render
 * inside this. The onboarding wall and the invitation page do NOT — they
 * keep the bare chrome, because someone on the wall has one job and
 * marketing links would be noise.
 *
 * Two things a browser does for a multi-page site that an SPA must do for
 * itself: scroll to the top on navigation (a footer link to a new page
 * must not land mid-page), and offer a skip link so a keyboard user is not
 * made to tab through the header on every page.
 */
export function PublicLayout({ children }: { children: ReactNode }): JSX.Element {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        overflowX: "hidden",
        bgcolor: "background.default",
        color: "text.primary",
        transition: "background-color 200ms, color 200ms",
      }}
    >
      <Link
        href="#main"
        sx={{
          position: "absolute",
          left: 16,
          top: -60,
          zIndex: 10,
          px: 2,
          py: 1,
          borderRadius: 1,
          bgcolor: "background.paper",
          color: "text.primary",
          boxShadow: 3,
          "&:focus-visible": { top: 12 },
        }}
      >
        Skip to content
      </Link>
      {/* Viewport-anchored: these pages are several screens tall, and a
          container-anchored backdrop would push its glows off the bottom of
          the document and centre its dot field on nothing. */}
      <Backdrop anchor="viewport" />
      <TopBar>
        <SiteNav />
      </TopBar>
      {/* `tabIndex={-1}` so the skip link actually MOVES focus: without it
          the browser jumps the scroll position to `#main` but leaves focus
          on the link, and the next Tab walks back into the header. */}
      <Box component="main" id="main" tabIndex={-1} sx={{ position: "relative", zIndex: 1, flex: 1, width: "100%", outline: "none" }}>
        {children}
      </Box>
      <SiteFooter siteMap={SITE_MAP} />
    </Box>
  );
}
